import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveCalendar } from '@nivo/calendar';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../theme';
import fetchData from '../helpers/fetchData';
import { useChartTheme } from '../themes/chartTheme';
import { FilterContext } from '../contexts/FilterContext';
import { dateToSqlDate } from '../helpers/dateFuncs';
import LoadingSpinner from './LoadingSpinner';

interface HeatmapData {
  value: number;
  day: string;
}

interface LeadGenerationHeatmapProps {
  selectedCustomers: string[];
  selectedClients: number[];
}

const LeadGenerationHeatmap: React.FC<LeadGenerationHeatmapProps> = ({
  selectedCustomers,
  selectedClients,
}) => {
  const chartTheme = useChartTheme();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [data, setData] = useState<HeatmapData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' });

  useEffect(() => {
    const fetchHeatmapData = async () => {
      if (selectedCustomers.length === 0 || selectedClients.length === 0) {
        setData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Always fetch full year data instead of just selected date range
        const currentYear = new Date().getFullYear();
        const yearStart = new Date(currentYear, 0, 1);
        const yearEnd = new Date(currentYear, 11, 31);

        const options = {
          start_date: dateToSqlDate(yearStart),
          end_date: dateToSqlDate(yearEnd),
          customers: selectedCustomers.join(','),
          clients: selectedClients.join(','),
        };

        const leadData = await fetchData<any[]>(
          '/api/daily-controlling/multi-customer-client-data',
          options,
        );

        const dailyTotals = new Map<string, number>();

        leadData.forEach((item) => {
          const date = item.date;
          dailyTotals.set(date, (dailyTotals.get(date) || 0) + (item.count || 0));
        });

        const heatmapData: HeatmapData[] = Array.from(dailyTotals.entries()).map(
          ([day, value]) => ({
            day,
            value,
          }),
        );

        setData(heatmapData);
        setDateRange({
          from: dateToSqlDate(yearStart),
          to: dateToSqlDate(yearEnd),
        });
      } catch (err: any) {
        console.error('Error fetching heatmap data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchHeatmapData();
  }, [selectedCustomers, selectedClients]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box p={2} color="error.main">
        Error loading heatmap: {error}
      </Box>
    );
  }

  if (selectedCustomers.length === 0 || selectedClients.length === 0) {
    return (
      <Box p={2} color="text.secondary" textAlign="center">
        Wählen Sie mindestens einen Kunden und Client aus, um Daten anzuzeigen.
      </Box>
    );
  }

  if (data.length === 0 || !dateRange.from || !dateRange.to) {
    return (
      <Box p={2} color="text.secondary" textAlign="center">
        Keine Daten für den ausgewählten Zeitraum verfügbar.
      </Box>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value), 1);
  const minValue = Math.min(...data.map((d) => d.value), 0);

  return (
    <Box height="250px">
      <ResponsiveCalendar
        data={data}
        from={dateRange.from}
        to={dateRange.to}
        theme={chartTheme}
        emptyColor={colors.primary[700]}
        colors={[
          colors.blueAccent[900],
          colors.blueAccent[800],
          colors.blueAccent[700],
          colors.blueAccent[600],
          colors.blueAccent[500],
          colors.blueAccent[400],
          colors.blueAccent[300],
        ]}
        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        yearSpacing={40}
        monthBorderColor={colors.primary[300]}
        dayBorderWidth={2}
        dayBorderColor={colors.primary[400]}
        legends={[
          {
            anchor: 'bottom-right',
            direction: 'row',
            translateY: 36,
            itemCount: 4,
            itemWidth: 42,
            itemHeight: 36,
            itemsSpacing: 14,
            itemDirection: 'right-to-left',
          },
        ]}
        tooltip={({ day, value, color }) => (
          <Box
            sx={{
              padding: '8px 12px',
              color: '#222',
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <strong>{new Date(day).toLocaleDateString('de-DE')}</strong>
            <br />
            Neue Leads: {value ? Number(value).toLocaleString('de-DE') : 0}
          </Box>
        )}
      />
    </Box>
  );
};

export default LeadGenerationHeatmap;
